// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
import React, { useState } from 'react';
import { Modal, Box } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmailIcon from '@mui/icons-material/Email';
import { SkillAssessmentLinkResponse } from '../../../../store/interviewHostSlice';
import styles from './candidates.module.scss';

interface SkillAssessmentLinkModalProps {
  open: boolean;
  onClose: () => void;
  assessmentData: SkillAssessmentLinkResponse;
  candidateName: string;
}

const SkillAssessmentLinkModal: React.FC<SkillAssessmentLinkModalProps> = ({
  open,
  onClose,
  assessmentData,
  candidateName
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopyLink = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(assessmentData.assessment.assessment_link);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch (error) {
      console.error('Failed to copy link:', error);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box className={styles.modal}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>Skill Assessment Link Sent</h2>
          <button className={styles.modalClose} onClick={onClose}>
            <CloseIcon />
          </button>
        </div>
        <div className={styles.modalBody}>
          <div className={styles.successMessage}>
            <CheckCircleIcon className={styles.successIcon} />
            <p>
              Skill assessment link has been successfully sent to <strong>{candidateName}</strong>
            </p>
          </div>

          <div className={styles.interviewDetails}>
            {assessmentData.email_status && (
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>
                  <EmailIcon sx={{ fontSize: 16, verticalAlign: 'middle', marginRight: '0.25rem' }} />
                  Email Status:
                </span>
                <span className={styles.detailValue}>{assessmentData.email_status}</span>
              </div>
            )}
          </div>

          <div className={styles.linkSection}>
            <label className={styles.linkLabel}>Skill Assessment Link</label>
            <div className={styles.linkBox}>
              <input
                type="text"
                className={styles.linkInput}
                value={assessmentData.assessment.assessment_link}
                readOnly
                onClick={(e) => e.currentTarget.select()}
              />
              <button className={copied ? styles.btnCopied : styles.btnCopy} onClick={handleCopyLink}>
                {copied ? (
                  <>
                    <CheckCircleIcon sx={{ fontSize: 16 }} />
                    Copied
                  </>
                ) : (
                  <>
                    <ContentCopyIcon sx={{ fontSize: 16 }} />
                    Copy
                  </>
                )}
              </button>
            </div>
            <p className={styles.linkHint}>Share this link with the candidate to start their skill assessment</p>
          </div>
        </div>
        <div className={styles.modalFooter}>
          <button className={styles.btnPrimary} onClick={onClose}>
            Done
          </button>
        </div>
      </Box>
    </Modal>
  );
};

export default SkillAssessmentLinkModal;
